<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('role.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('role.pageTitle'),
            href: '/roles'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="ts-flex ts-items-center ts-justify-end">
            <label class="required ts-pr-4">{{ $t('role.roleName') }}</label>
            <div style="width:300px">
              <input
                v-model.trim="model.name"
                type="text"
                class="ts-border ts-border-gray-400 ts-w-full ts-rounded-md ts-px-3 ts-py-2 ts-outline-none"
                :class="{ 'is-invalid': errors.has('name') }"
                autofocus
              />
              <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
            </div>
          </div>
          <form class="form-horizontal">
            <div class="row ts-mt-4">
              <div class="col-md-12">
                <div id="container">
                  <table class="table table-hover ui-table ts-table">
                    <thead>
                      <tr>
                        <th class="ts-sticky ts-top-0 ts-z-10">
                          <input
                            v-model="searchPermissionText"
                            type="text"
                            style="width:300px"
                            class="form-control"
                            :placeholder="$t('search')"
                          />
                        </th>
                        <th
                          v-for="column in permissionColumns"
                          :key="column"
                          class="ts-sticky ts-top-0 ts-z-10 ts-w-20 ts-text-center"
                        >
                          <div
                            class="ts-flex ts-flex-col ts-justify-center ts-items-center ts-w-full"
                          >
                            <p class="ts-mb-0">{{ column }}</p>
                            <ts-checkbox
                              :checkedValue="allCheckedBy(column)"
                              @change="checked => selectAllBy(column, checked)"
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(group, groupName) in permissionGroups">
                        <tr>
                          <td>
                            <span
                              class="ts-text-sm ts-font-semibold ts-pt-6 ts-inline-block"
                              >{{ groupName }}</span
                            >
                          </td>
                          <td v-for="n in permissionColumns.length" :key="n">
                            <div class="ts-flex ts-justify-center ts-pt-6">
                              <ts-checkbox
                                :checkedValue="
                                  allCheckedByGroup(
                                    groupName,
                                    permissionColumns[n - 1]
                                  )
                                "
                                @change="
                                  checked =>
                                    selectByGroup(
                                      groupName,
                                      permissionColumns[n - 1],
                                      checked
                                    )
                                "
                              />
                            </div>
                          </td>
                        </tr>
                        <tr
                          v-for="(permissions, resourceName) in group"
                        >
                          <td>
                            <span class="ts-ml-4 ts-mr-6">{{
                              resourceName
                            }}</span>
                          </td>
                          <td v-for="n in permissionColumns.length" :key="n">
                            <div class="ts-flex ts-justify-center">
                              <ts-checkbox
                                v-model="model.permissions"
                                :value="isShow(permissions, n).id"
                                v-if="isShow(permissions, n) !== undefined"
                              />
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-12 text-right">
                <ts-button
                  class="mr-2"
                  @click.prevent="$router.push({ name: 'role' })"
                  >{{ $t('cancel') }}</ts-button
                >
                <ts-button
                  color="primary"
                  :waiting="waiting"
                  @click.prevent="update"
                  >{{ $t('update') }}</ts-button
                >
              </div>
            </div>
          </form>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState, mapGetters } from 'vuex'
import {
  cloneDeep,
  union,
  uniq,
  difference,
  intersection,
  flatMap,
  map,
  filter
} from 'lodash'

export default {
  name: 'roleEdit',
  data() {
    return {
      model: {
        id: 0,
        name: '',
        permissions: []
      },
      loading: false,
      waiting: false,
      errors: new Errors(),
    }
  },
  created() {
    this.fetchPermissions({
      guard_name: this.edit_data.guard_name
    })
    this.setEditData()
  },
  computed: {
    ...mapState('security/role', ['permissions', 'edit_data']),
    ...mapGetters('security/role', ['permissionGroups', 'permissionColumns']),
    searchPermissionText: {
      get() {
        return this.$store.state.security.role.searchPermissionText
      },
      set(newValue) {
        this.$store.commit('security/role/SET_SEARCH_PERMISSION', newValue)
      }
    },
    idPermissionsBy: vm => column => {
      return uniq(
        map(
          filter(vm.permissions, p => {
            return p.name.toLowerCase().startsWith(column.toLowerCase())
          }),
          'id'
        )
      )
    },
    allCheckedBy: vm => column => {
      const idPermissions = vm.idPermissionsBy(column)

      return (
        intersection(vm.model.permissions, idPermissions).length ===
        idPermissions.length
      )
    },
    idPermissionsGroup: vm => (groupName, column) => {
      return uniq(
        map(
          filter(flatMap(vm.permissionGroups[groupName]), p => {
            return p.name.toLowerCase().startsWith(column.toLowerCase())
          }),
          'id'
        )
      )
    },
    allCheckedByGroup: vm => (groupName, column) => {
      const idPermissions = vm.idPermissionsGroup(groupName, column)

      return (
        intersection(vm.model.permissions, idPermissions).length ===
        idPermissions.length
      )
    },
    isShow: vm => (permission, pOrder) => {
      switch(pOrder){
        case 1:
          return permission.filter(p => p.name.toLowerCase().indexOf('view') >= 0)[0]
        case 2:
          return permission.filter(p => p.name.toLowerCase().indexOf('create') >= 0)[0]
        
        case 3:
          return permission.filter(p => p.name.toLowerCase().indexOf('update') >= 0)[0]
        
        case 4:
          return permission.filter(p => p.name.toLowerCase().indexOf('delete') >= 0)[0]
        
        default :
          return undefined
      }
    }
  },
  methods: {
    fetchPermissions(attributes) {
      this.loading = true

      this.$store
        .dispatch('security/role/fetchPermissions', attributes)
        .finally(() => (this.loading = false))
    },
    selectAllBy(column, checked) {
      if (checked) {
        this.model.permissions = union(
          this.model.permissions,
          this.idPermissionsBy(column)
        )
      } else {
        this.model.permissions = difference(
          this.model.permissions,
          this.idPermissionsBy(column)
        )
      }
    },
    selectByGroup(groupName, column, checked) {
      if (checked) {
        this.model.permissions = union(
          this.model.permissions,
          this.idPermissionsGroup(groupName, column)
        )
      } else {
        this.model.permissions = difference(
          this.model.permissions,
          this.idPermissionsGroup(groupName, column)
        )
      }
    },
    filterPermission() {
      this.model.name = ''
      this.model.permissions = []
      this.fetchPermissions({
        guard_name: this.model.guard_name
      })
    },
    setEditData() {
      this.model.id = this.edit_data.id
      this.model.name = this.edit_data.name
      this.model.permissions = this.edit_data.permissions.map(el => el.id)
    },
    update() {
      this.waiting = true
      this.$store
        .dispatch('security/role/update', {
          id: this.model.id,
          ...this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'role' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('security/role/SET_EDIT_DATA', [])
    next()
  }
}
</script>

<style lang="css" scoped>
#container {
  min-height: 40vh;
  max-height: 60vh;
  overflow-y: scroll;
}

.table > thead > tr > th,
.table > tbody > tr > th,
.table > tfoot > tr > th,
.table > thead > tr > td,
.table > tbody > tr > td,
.table > tfoot > tr > td {
  border-color: #e2e7eb;
  padding: 4px 2px !important;
}
</style>
